import { Badge } from "@/components/ui/badge";
import { Link } from "wouter";
import type { PhoneTest, PhoneTestStatus } from "@workspace/api-client-react";

const STATUS_STYLES: Record<PhoneTestStatus, string> = {
  sent: "border-blue-500/30 text-blue-400 bg-blue-500/10",
  rechecked: "border-green-500/30 text-green-500 bg-green-500/10",
  failed: "border-red-500/30 text-red-500 bg-red-500/10",
  queued: "border-amber-500/30 text-amber-500 bg-amber-500/10",
};

const STATUS_LABELS: Record<PhoneTestStatus, string> = {
  sent: "Sent",
  rechecked: "Rechecked",
  failed: "Failed",
  queued: "Queued",
};

export function PhoneTestStatusBadge({ status }: { status: PhoneTestStatus }) {
  return (
    <Badge
      variant="outline"
      className={`uppercase font-mono text-[10px] ${STATUS_STYLES[status]}`}
    >
      {STATUS_LABELS[status]}
    </Badge>
  );
}

function fmt(value?: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

export function PhoneTestsTable({
  tests,
  showAccount = false,
  emptyLabel = "No phone-test attempts yet.",
}: {
  tests: PhoneTest[];
  showAccount?: boolean;
  emptyLabel?: string;
}) {
  const colCount = showAccount ? 6 : 5;
  return (
    <div className="border border-border rounded overflow-x-auto custom-scrollbar">
      <table className="w-full text-xs min-w-[560px]">
        <thead className="bg-muted text-muted-foreground">
          <tr>
            {showAccount && (
              <th className="text-left py-2 px-3 font-medium">Account</th>
            )}
            <th className="text-left py-2 px-3 font-medium">Status</th>
            <th className="text-left py-2 px-3 font-medium">Sent</th>
            <th className="text-left py-2 px-3 font-medium">Recheck</th>
            <th className="text-left py-2 px-3 font-medium">Twilio SID / Error</th>
            <th className="text-left py-2 px-3 font-medium">By</th>
          </tr>
        </thead>
        <tbody>
          {tests.map((t) => (
            <tr key={t.id} className="border-t border-border align-top">
              {showAccount && (
                <td className="py-1.5 px-3">
                  <Link
                    href={`/accounts/${t.accountId}`}
                    className="text-primary hover:underline"
                  >
                    {t.accountName || t.vendorAccountEmail || `#${t.accountId}`}
                  </Link>
                </td>
              )}
              <td className="py-1.5 px-3">
                <PhoneTestStatusBadge status={t.status} />
              </td>
              <td className="py-1.5 px-3 whitespace-nowrap">
                {fmt(t.sentAt ?? t.createdAt)}
              </td>
              <td className="py-1.5 px-3 whitespace-nowrap">
                {t.recheckedAt ? (
                  <span className="text-green-500">{fmt(t.recheckedAt)}</span>
                ) : t.recheckAt ? (
                  <span className="text-muted-foreground">
                    Due {fmt(t.recheckAt)}
                  </span>
                ) : (
                  "—"
                )}
              </td>
              <td className="py-1.5 px-3 font-mono break-all">
                {t.status === "failed" && t.errorMessage ? (
                  <span className="text-red-500">{t.errorMessage}</span>
                ) : t.twilioSid ? (
                  t.twilioSid
                ) : (
                  "—"
                )}
              </td>
              <td className="py-1.5 px-3 truncate max-w-[160px]">
                {t.actorEmail || "—"}
              </td>
            </tr>
          ))}
          {tests.length === 0 && (
            <tr>
              <td
                colSpan={colCount}
                className="text-center py-6 text-muted-foreground"
              >
                {emptyLabel}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
